import { CombinedGraphQLErrors } from "@apollo/client/errors";

/** Response paths for each GraphQL error, when present. */
export function getGraphQLErrorPaths(error: unknown): ReadonlyArray<readonly (string | number)[]> {
    if (!CombinedGraphQLErrors.is(error)) return [];

    return error.errors.map((entry) => entry.path).filter((path): path is NonNullable<typeof path> => Array.isArray(path));
}

/** True when any error path hits `propertyByBFENumber.<field>`. */
export function hasPropertyFieldError(error: unknown, fields: readonly string[]): boolean {
    return getGraphQLErrorPaths(error).some((path) => {
        if (path[0] !== "propertyByBFENumber") return false;
        const field = path[1];
        return typeof field === "string" && fields.includes(field);
    });
}

export const PROPERTY_ECONOMY_CORE_FIELDS = ["valuations", "valuationProperties", "ownerChanges"] as const;
export const PROPERTY_DANISH_LAND_REGISTER_FIELD = "danishLandRegister";

export function hasPropertyEconomyCoreError(error: unknown): boolean {
    return hasPropertyFieldError(error, PROPERTY_ECONOMY_CORE_FIELDS);
}

export function hasPropertyDanishLandRegisterError(error: unknown): boolean {
    return hasPropertyFieldError(error, [PROPERTY_DANISH_LAND_REGISTER_FIELD]);
}

/** Tinglysning outages are expected for a single field — degrade UI instead of alarming. */
export function isExpectedTinglysningGraphQLError(error: {
    path?: readonly (string | number)[] | null;
    message?: string;
    extensions?: Record<string, unknown> | null;
}): boolean {
    const path = error.path;
    const rootField = path?.[0];
    const message = error.message ?? "";
    if (rootField === "dlrDocumentSnapshotState" && message === "DLR document snapshot state unavailable") return true;
    if (rootField === "dlrDocumentRefreshJob" && (message === "DLR document refresh job not found" || message === "DLR document refresh job unavailable")) {
        return true;
    }
    if (rootField === "requestDlrDocumentSnapshotRefresh" && message === "DLR document refresh could not be requested") return true;
    if (!path?.includes(PROPERTY_DANISH_LAND_REGISTER_FIELD)) return false;

    const code = error.extensions?.code;
    return code === "DOWNSTREAM_SERVICE_ERROR" || message.includes("TINGLYSNING") || message.includes("THIRD_PARTY_SERVICE_ERROR");
}

/** True when every GraphQL error in the batch is an expected Tinglysning downstream failure. */
export function isTinglysningDownstreamGraphQLError(error: unknown): boolean {
    if (!CombinedGraphQLErrors.is(error)) return false;

    return error.errors.length > 0 && error.errors.every((entry) => isExpectedTinglysningGraphQLError(entry));
}
